import React, { useEffect, useState } from "react";
import {
  TextField, Button, Switch, FormControlLabel, FormControl, Grid, Box, InputAdornment,
  Paper, Card, CardContent, Typography, IconButton, Tooltip, Stack, Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import { LedgerQueryViewModel, ItemLedgerViewModel } from "../../Types/ExportTypes";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import ReportHeader from "../../components/Ledger/LedgerReportHeader";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import { ItemAutoComplete, LocationAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../../components/ResponsivePagination";
const ItemLedgerPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { t } = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();

  const { goBack } = useNavigation();
  const [filter, setFilter] = useState<string>("");
  const [filterProperty, setFilterProperty] = useState<string>("");
  const [query, setQuery] = useState<LedgerQueryViewModel>({
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
  });
  const [items, setItems] = useState<ItemLedgerViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [TotalGraphSum, setTotalGraphSum] = useState(0);
  const [totalGraphQty, setTotalGraphQty] = useState(0);
  const [initialized, setInitialized] = useState(false);



  const fetchDocuments = async () => {
    showLoading();
    try {
      const response = await authService.post<{ items: ItemLedgerViewModel[]; count: number }>(
        `/inventoryApi/GetItemLedger`,
        query
      );
      setItems(response.items);
      setTotalCount(response.count)


    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [query]);




  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // if (!filter) return;

    var localFilterProperty = filterProperty;
    // if (!filter) return;
    if (!localFilterProperty?.trim() && filter?.trim()) {
      localFilterProperty = "Default";
    }

    if (localFilterProperty?.trim() && filter?.trim()) {
      const newFilter = buildQueryFilter(localFilterProperty, filter);
      const isDuplicate = query.filters?.some(f =>
        f.property === newFilter.property && f.value === newFilter.value
      );
      if (isDuplicate) return;

      const newFilters = [...(query.filters ?? []), newFilter];
      setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
    } else {
      // No filterProperty: just update the query with the raw filter string
      setQuery({ ...query, page: 0, filter });
    }

    setFilter("");
    setFilterProperty("");
  };
  const handlePageChange = (newPage: number) => {
    setQuery({ ...query, page: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setQuery({ ...query, pageSize: newPageSize, page: 0 });
  };



  const setHeaderFilters = async (updated: LedgerQueryViewModel) => {
    setQuery({ ...updated, filters: query.filters });
  };


  function generateVariantLabel(line: ItemLedgerViewModel) {
    if (!line.variantId) {
      return t("common.none");
    }

    const parts = [];


    if (line.colourDescription) {
      parts.push(line.colourDescription);
    }


    if (line.sizeDescription) {
      parts.push(line.sizeDescription);
    }


    if (line.lengthDescription) {
      parts.push(line.lengthDescription);
    }


    const details = parts.join(" | ");


    return ` ${details || "No Details Available"}`;
  }

  return (
    <>
      <CenteredCard title={t("common.itemLedger")} isLoading={false}>
        <ReportHeader filter={query} onGenerate={setHeaderFilters} />
        <Paper sx={{ padding: 2 }}>
          {/* Search Field */}
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={5} lg={2}>
                <TextField
                  select
                  label={t("common.field")}
                  value={filterProperty}
                  onChange={(e) => setFilterProperty(e.target.value)}
                  SelectProps={{ native: true }}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">{t("common.default")}</option>
                  <option value="ext_DocumentNo">{t("common.documentNo")}</option>
                  <option value="ext_ItemNo">{t("common.item")}</option>
                  <option value="ext_LocationCode">{t("common.location")}</option>
                </TextField>
              </Grid>
              <Grid item xs={7} lg={7}>
                {(() => {
                  switch (filterProperty) {
                    case "ext_LocationCode":
                      return (
                        <LocationAutoComplete
                          onChange={(o) => setFilter(o?.value || "")}
                        />
                      );
                    case "ext_ItemNo":
                      return (
                        <ItemAutoComplete
                          onChange={(o) => setFilter(o?.value || "")}
                        />
                      )
                    default:
                      return (
                        <TextField
                          label={t("common.search")}
                          variant="outlined"
                          fullWidth
                          onChange={(event) => setFilter(event.target.value)}
                          value={filter}

                        />
                      );
                  }
                })()}
                {/*<TextField*/}
                {/*    label={t("common.search")}*/}
                {/*    variant="outlined"*/}
                {/*    fullWidth*/}
                {/*    onChange={(event) => setFilter(event.target.value)}*/}
                {/*    value={filter}*/}
                {/*/>*/}
              </Grid>
              <Grid item xs={12} lg={3}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  {t("common.search")}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {query.filters?.map((f, index) => (
              <Chip
                key={index}
                label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                onDelete={() => {
                  const newFilters = query.filters?.filter((_, i) => i !== index);
                  setQuery({ ...query, filters: newFilters, page: 0 });
                }}
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>
          {/* List Layout for Items */}
          <Stack spacing={2}>
            {items.map((item) => (
              <Paper
                key={item.entryNo}
                sx={{
                  p: 2,
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.3s',
                  '&:hover': { backgroundColor: '#f0f0f0' }
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {item.entryNo}
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary">
                      {t('common.postingDate')}:
                    </Typography>
                    <Typography variant="body1">
                      {item.postingDate?.split('T')[0]}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary">
                      {t('common.documentNo')}:
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {item.documentNo}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary">
                      {t('common.location')}:
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {item.locationCode} - {item.locationName}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary">
                      {t('common.item')}:
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {item.itemNo} - {item.description}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary">
                      {t('common.variant')}:
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {generateVariantLabel(item)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" fontWeight="bold">
                      {t('common.quantity')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.quantity?.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" fontWeight="bold">
                      {t('common.remainingQuantity')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.remainingQty?.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant="body2" fontWeight="bold">
                      {t('common.value')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.costAmountActualLCY?.toLocaleString()}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            ))}
            {items.length === 0 && (
              <Typography align="center">{t('common.noEntriesFound')}</Typography>
            )}
          </Stack>
          <ResponsivePagination
            sx={{ mt: 2 }}
            page={query.page}

            pageSize={query.pageSize}

            totalCount={totalCount}

            onPageChange={handlePageChange}

          />
        </Paper>
      </CenteredCard>


    </>
  );
};

export default ItemLedgerPage;